// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import { useHoleChecks, type HoleCheckFinding, type HoleCheckHole } from './useHoleChecks';

/** Kinds and sources this build has words for; anything else is shown raw rather than as a key. */
const KINDS = ['tee', 'fairway', 'green', 'bunker', 'water', 'trees'];
const SOURCES = ['manual', 'gps', 'osm', 'lantmateriet', 'satellite', 'derived'];
const RULES = [
  'one-green',
  'tee-distance',
  'tee-back-half',
  'fairway-missing',
  'trees-on-play',
  'cover-trimmed',
  'cover-covered',
];
const HOLE_KINDS = ['tee', 'fairway', 'green'] as const;

/**
 * A course's per-hole to-do list for an admin: what each hole shows players and from whom,
 * what it is missing, and what the latest analysis held back or flagged for a person to look at.
 */
export function HoleChecksSection({
  apiBaseUrl,
  token,
  courseId,
}: {
  apiBaseUrl: string;
  token: string | null | undefined;
  courseId: string;
}) {
  const { t } = useI18n();
  const { report, loading, error } = useHoleChecks(apiBaseUrl, token, courseId);

  return (
    <View style={styles.card} testID="hole-checks">
      <Text style={styles.title}>{t('courseAnalysis.holeChecks.title')}</Text>
      <Text style={styles.meta}>{t('courseAnalysis.holeChecks.intro')}</Text>

      {loading && !report ? (
        <ActivityIndicator color={appColors.accent.primary} />
      ) : error === 'unavailable' ? (
        <Text style={styles.meta}>{t('courseAnalysis.holeChecks.unavailable')}</Text>
      ) : error ? (
        <Text style={styles.meta}>{t('courseAnalysis.holeChecks.failed')}</Text>
      ) : report ? (
        <>
          <Text style={styles.summary} testID="hole-checks-summary">
            {report.checkedAt === null
              ? t('courseAnalysis.holeChecks.notChecked')
              : t('courseAnalysis.holeChecks.summary', {
                  held: report.held,
                  flagged: report.flagged,
                  incomplete: report.incomplete,
                })}
          </Text>
          {report.holes.map((hole) => (
            <HoleRow key={hole.holeNumber} hole={hole} />
          ))}
        </>
      ) : null}
    </View>
  );
}

function HoleRow({ hole }: { hole: HoleCheckHole }) {
  const { t } = useI18n();
  const kind = (value: string) => (KINDS.includes(value) ? t(`courseAnalysis.kind.${value}`) : value);
  const source = (value: string) => (SOURCES.includes(value) ? t(`courseAnalysis.source.${value}`) : value);

  return (
    <View style={styles.hole} testID={`hole-checks-hole-${hole.holeNumber}`}>
      <Text style={styles.holeTitle}>
        {t('courseAnalysis.holeTitle', { hole: hole.holeNumber })}
        {hole.par === null ? null : ` · ${t('courseAnalysis.holeChecks.par', { par: hole.par })}`}
      </Text>
      {HOLE_KINDS.map((k) => (
        <Text key={k} style={styles.meta}>
          {t('courseAnalysis.holeChecks.featureLine', {
            kind: kind(k),
            sources: hole[k].length === 0 ? t('courseAnalysis.holeChecks.none') : hole[k].map(source).join(', '),
          })}
        </Text>
      ))}
      {hole.missing.length > 0 ? (
        <Text style={styles.warning} testID={`hole-checks-missing-${hole.holeNumber}`}>
          {t('courseAnalysis.holeChecks.missing', { kinds: hole.missing.map(kind).join(', ') })}
        </Text>
      ) : null}
      {hole.findings.map((finding, index) => (
        <FindingLine key={`${finding.rule}-${finding.featureId ?? index}`} finding={finding} kind={kind} source={source} />
      ))}
    </View>
  );
}

function FindingLine({
  finding,
  kind,
  source,
}: {
  finding: HoleCheckFinding;
  kind: (value: string) => string;
  source: (value: string) => string;
}) {
  const { t } = useI18n();
  const rule = RULES.includes(finding.rule) ? t(`courseAnalysis.holeChecks.rule.${finding.rule}`) : finding.rule;

  if (finding.removedShare !== null) {
    return (
      <Text style={styles.held}>
        {t('courseAnalysis.holeChecks.trimmed', {
          rule,
          kind: kind(finding.kind),
          share: `${Math.round(finding.removedShare * 100)}%`,
        })}
      </Text>
    );
  }

  if (finding.held) {
    return (
      <Text style={styles.held}>
        {t('courseAnalysis.holeChecks.held', {
          rule,
          kind: kind(finding.kind),
          before: finding.confidenceBefore?.toFixed(2) ?? '–',
          after: finding.confidenceAfter?.toFixed(2) ?? '–',
        })}
      </Text>
    );
  }

  return (
    <Text style={styles.warning}>
      {finding.source === null
        ? t('courseAnalysis.holeChecks.flaggedHole', { rule })
        : t('courseAnalysis.holeChecks.flagged', { rule, source: source(finding.source), kind: kind(finding.kind) })}
    </Text>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 14,
    gap: 8,
  },
  title: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
  summary: { color: appColors.text.primary, fontSize: 13, fontWeight: '600' },
  hole: {
    gap: 2,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: appColors.surface.input,
  },
  holeTitle: { color: appColors.text.primary, fontSize: 13, fontWeight: '700' },
  meta: { color: appColors.text.secondary, fontSize: 12 },
  held: { color: appColors.text.secondary, fontSize: 12, fontStyle: 'italic' },
  warning: { color: appColors.accent.goldDeep, fontSize: 12 },
});
